/**
 * Email Service - Sends form notifications via multiple methods
 * Priority: 1. EmailJS (browser-based), 2. PHP backend, 3. Silent fail (data saved to DB)
 */
import emailjs from '@emailjs/browser';

// EmailJS Configuration
// Sign up free at https://www.emailjs.com/ to get these values
const EMAILJS_SERVICE_ID = 'service_automax';
const EMAILJS_PUBLIC_KEY = 'YOUR_EMAILJS_PUBLIC_KEY'; // Replace with your EmailJS public key

// Template IDs
const EMAILJS_CONTACT_TEMPLATE = 'template_contact';
const EMAILJS_QUOTE_TEMPLATE = 'template_quote';
const EMAILJS_INQUIRY_TEMPLATE = 'template_inquiry';

// Initialize EmailJS
let emailjsInitialized = false;
function initEmailJS() {
    if (!emailjsInitialized && EMAILJS_PUBLIC_KEY !== 'YOUR_EMAILJS_PUBLIC_KEY') {
        emailjs.init(EMAILJS_PUBLIC_KEY);
        emailjsInitialized = true;
    }
}

interface EmailData {
    name: string;
    email: string;
    phone: string;
    message: string;
    type?: string;
}

/**
 * Attempt to send email notification. Returns true if sent, false if failed.
 * This function NEVER throws - it always returns gracefully.
 */
export async function sendFormEmail(data: EmailData, formType: 'contact' | 'quote' | 'inquiry' = 'contact'): Promise<boolean> {
    // Method 1: Try EmailJS (works everywhere, no server needed)
    const emailjsSent = await tryEmailJS(data, formType);
    if (emailjsSent) return true;

    // Method 2: Try PHP backend (works on production servers with PHP)
    const phpSent = await tryPHPBackend(data, formType);
    if (phpSent) return true;

    // Method 3: All email methods failed - data is still saved to Firestore by the calling component
    console.info('[Email Service] Email delivery deferred. Inquiry saved to database for admin review.');
    return false;
}

async function tryEmailJS(data: EmailData, formType: string): Promise<boolean> {
    try {
        initEmailJS();
        if (!emailjsInitialized) return false;

        const templateId = formType === 'contact' ? EMAILJS_CONTACT_TEMPLATE
            : formType === 'quote' ? EMAILJS_QUOTE_TEMPLATE
                : EMAILJS_INQUIRY_TEMPLATE;

        const templateParams = {
            from_name: data.name,
            from_email: data.email,
            phone: data.phone,
            message: data.message,
            type: data.type || formType,
            to_email: 'sales@automaxspace.com',
        };

        await emailjs.send(EMAILJS_SERVICE_ID, templateId, templateParams);
        console.log('[Email Service] Sent via EmailJS');
        return true;
    } catch (error) {
        console.warn('[Email Service] EmailJS failed:', error);
        return false;
    }
}

async function tryPHPBackend(data: EmailData, formType: string): Promise<boolean> {
    try {
        const endpoint = formType === 'contact' ? '/send_email.php' : '/send_sales_email.php';

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000); // 5 second timeout

        const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
            signal: controller.signal,
        });

        clearTimeout(timeout);

        const text = await response.text();

        // Detect if Vite served index.html instead of PHP (common in dev)
        if (text.includes('<!DOCTYPE') || text.includes('<html')) {
            console.warn('[Email Service] PHP not available (Vite dev server detected)');
            return false;
        }

        const result = JSON.parse(text);
        if (result.success) {
            console.log('[Email Service] Sent via PHP backend');
            return true;
        }

        console.warn('[Email Service] PHP backend error:', result.message);
        return false;
    } catch (error) {
        console.warn('[Email Service] PHP backend unreachable:', error);
        return false;
    }
}

export default sendFormEmail;
